import { PLOT_TYPE_OPTIONS as plotTypeOptions } from "../../../../constants/visualizationOptions";
import React, { useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import Label from "../../../../components/Label/Label";
import "../batchFIlters/BatchFilterPanel.scss";

import { useDispatch, useSelector } from "react-redux";
import {
  clearParameterList,
  clearProductsSitesAndUnitOfOperations,
  fetchParameterList,
} from "../../../../redux/visualization/actions";
import {
  clearProductSitesUopFilters,
  setProductSitesUopFilters,
} from "../../../../redux/shared/actions";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const renderCardDropdown = ({
  label,
  value,
  list,
  onChange,
  disabled,
  fullWidth = true,
}) => {
  return (
    <Card elevation={0} className="filter-card">
      <CardContent>
        <Label text={label} fontWeight="500" />

        <FormControl
          fullWidth={fullWidth}
          size="small"
          className="dropdown-field"
          disabled={disabled}
        >
          <InputLabel>{`Select ${label}`}</InputLabel>

          <Select
            value={value?.id || ""}
            label={`Select ${label}`}
            onChange={(e) => {
              const selected = list.find((i) => i?.id === e.target.value);

              onChange(
                selected
                  ? { id: selected?.id, name: selected.name }
                  : { id: "", name: "" },
              );
            }}
          >
            {list.map((item) => (
              <MenuItem key={item?.id} value={item?.id}>
                {item.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </CardContent>
    </Card>
  );
};

const ParameterFilterPanel = ({
  updateState,
  onClickClearAllFilters,
  batchState,
  disableAllFields,
  isUnitOpHide,
  disabledFromParent = false,
  setShowConfigPanel,
  parameterState,
  setSelectedTab,
}) => {
  const dispatch = useDispatch();

  const { product, site, unitOfOperation } = parameterState;

  // const {
  //   product = { id: "", name: "" },
  //   site = { id: "", name: "" },
  //   unitOfOperation = { id: "", name: "" },
  // } = useSelector((state) => state.shared.productSitesUopValues || {});

  // const [configErrors, setConfigErrors] = useState({});
  // const [batchErrors, setBatchErrors] = useState({});
  const {
    data: parameterData,
    loader: parameterLoader,
    apiError: parameterError,
    message: parameterMessage,
    apiSuccess: parameterSuccess,
  } = useSelector((state) => state.viz.parameterNamesList || {});

  const { data = [] } = useSelector((state) => {
    const list = state?.viz?.productsAndSitesList;
    return { data: Array.isArray(list?.data) ? list.data : [] };
  });

  useEffect(() => {
    if (
      parameterState?.parameterState?.product?.id ||
      parameterState?.parameterState?.site?.id
    ) {
      // Clear parameter list & batch list
      dispatch(clearParameterList());
    }
  }, [parameterState?.product?.id, parameterState?.site?.id, dispatch]);

  useEffect(() => {
    if (
      parameterState?.product?.id &&
      parameterState?.site?.id &&
      parameterState?.unitOfOperation?.id
    ) {
      const payload = {
        drug_product_id: parameterState?.product?.id,
        site_id: parameterState?.site?.id,
        unit_op_id: parameterState?.unitOfOperation?.id,
      };
      dispatch(fetchParameterList(payload));
    }
  }, [
    parameterState?.product?.id,
    parameterState?.site?.id,
    parameterState?.unitOfOperation?.id,
    dispatch,
  ]);

  useEffect(() => {
    return () => {
      dispatch(clearParameterList());
      // dispatch(clearProductSitesUopFilters())
      // dispatch(clearProductsSitesAndUnitOfOperations());
    };
  }, []);

  const productList = useMemo(
    () => data.map((p) => ({ id: p.product_id, name: p.product_name })),
    [data],
  );

  const sitesList = useMemo(() => {
    if (!parameterState?.product?.id) return [];
    const matched = data.find(
      (p) => p.product_id === parameterState?.product?.id,
    );
    return (
      matched?.sites?.map((s) => ({ id: s.site_id, name: s.site_name })) || []
    );
  }, [data, parameterState?.product]);

  const unitOfOperationList = useMemo(() => {
    if (!parameterState?.product?.id || !parameterState?.site?.id) return [];
    const matchedProduct = data.find(
      (p) => p.product_id === parameterState?.product?.id,
    );
    const matchedSite = matchedProduct?.sites?.find(
      (s) => s.site_id === parameterState?.site?.id,
    );
    return (
      matchedSite?.uom?.map((u) => ({ id: u.unit_op_id, name: u.step_name })) ||
      []
    );
  }, [data, parameterState?.product, parameterState?.site]);

  return (
    <Box className="batch-filter-panel">
      <Box className="scrollable-content">
        {/* Product */}
        {renderCardDropdown({
          label: "Product",
          value: parameterState?.product,
          list: productList,
          disabled: disabledFromParent,
          onChange: (value) =>
            // dispatch(
            //   setProductSitesUopFilters({
            //     product: value,
            //     site: { id: "", name: "" },
            //     unitOfOperation: { id: "", name: "" },
            //   }),
            // ),
            updateState("siteUnitParameter", {
              product: value,
              site: { id: "", name: "" },
              unitOfOperation: { id: "", name: "" },
            }), // store whole object
        })}

        {/* Manufacturing Site */}
        {renderCardDropdown({
          label: "Manufacturing Site",
          value: parameterState?.site,
          list: sitesList,
          disabled: !parameterState?.product?.id || disabledFromParent,
          onChange: (value) =>
            // dispatch(
            //   setProductSitesUopFilters({
            //     product,
            //     site: value,
            //     unitOfOperation: { id: "", name: "" },
            //   }),
            // ),
            updateState("siteUnitParameter", {
              product,
              site: value,
              // unitOfOperation: { id: "", name: "" },
            }), // store whole object
        })}

        {/* Unit Operation */}
        {!isUnitOpHide &&
          renderCardDropdown({
            label: "Unit Operation",
            value: parameterState?.unitOfOperation,
            list: unitOfOperationList,
            disabled: !parameterState?.site?.id || disabledFromParent,
            onChange: (value) =>
              // dispatch(
              //   setProductSitesUopFilters({
              //     product,
              //     site,
              //     unitOfOperation: value,
              //   }),
              // ),
              updateState("siteUnitParameter", {
                product,
                site,
                unitOfOperation: value,
              }), // store whole object
          })}

        {/* Parameter Selection */}
        <Card elevation={0} className="filter-card">
          <CardContent>
            <Label text="Parameter" fontWeight="500" />
            <FormControl
              fullWidth
              size="small"
              className="dropdown-field"
              disabled={disableAllFields}
            >
              <InputLabel>Select Parameter</InputLabel>
              <Select
                value={parameterState?.parameter?.parameterId || ""} // so it matches MenuItem value
                onChange={(e) => {
                  const selectedParam = parameterData?.find(
                    (p) => p.parameter_id === e.target.value,
                  );
                  updateState("parameter", {
                    label: selectedParam.label,
                    parameterId: selectedParam.parameter_id,
                    parameterName: selectedParam.parameter_name,
                    control_limit_options: selectedParam.control_limit_options || [],
                    specification_limit_options:
                      selectedParam.specification_limit_options || [],
                  }); // store whole object
                }}
                label="Select Parameter"
              >
                {parameterData?.map((param) => (
                  <MenuItem key={param.parameter_id} value={param.parameter_id}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <Label text={param.label} fontWeight={500}></Label>
                      <Tooltip
                        title={
                          <Box>
                            <Label
                              text={`Phase - ${param.phase || "N/A"}`}
                              color="inherit"
                              fontWeight={500}
                            />
                            <Label
                              text={`Sub Phase - ${param.sub_phase || "N/A"}`}
                              color="inherit"
                              fontWeight={500}
                            />

                            <Label
                              text={`Level - ${param.table_level || "N/A"}`}
                              color="inherit"
                              fontWeight={500}
                            />
                          </Box>
                        }
                        arrow
                        placement="right"
                      >
                        <InfoOutlinedIcon
                          fontSize="small"
                          sx={{ color: "#777", ml: 1 }}
                        />
                      </Tooltip>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </CardContent>
        </Card>

        {/* Plot Type */}
        <Card elevation={0} className="filter-card">
          <CardContent>
            <Label text="Plot Type" fontWeight="500" />
            <FormControl
              fullWidth
              size="small"
              className="dropdown-field"
              disabled={disableAllFields}
            >
              <InputLabel>Plot Type</InputLabel>
              <Select
                value={parameterState?.plotType || ""}
                onChange={(e) => updateState("plotType", e.target.value)}
                label="Plot Type"
              >
                {plotTypeOptions.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </CardContent>
        </Card>
      </Box>

      {/* Sticky Footer */}
      <Box className=" footer-sticky ">
        <Card elevation={0} className=" filter-card plot-footer-btn-card">
          <CardContent>
            <Box className="footer-btn-wrapper">
              <Button
                variant="outlined"
                className="close-btn"
                onClick={() => setShowConfigPanel(false)}
              >
                Close
              </Button>

              <Button
                variant="outlined"
                onClick={onClickClearAllFilters}
                disabled={disableAllFields}
              >
                Clear All
              </Button>
              <Tooltip title="Next">
                <IconButton
                  onClick={() => setSelectedTab("batchFilters")}
                  size="small"
                  sx={{
                    backgroundColor: "primary.main",
                    color: "#fff",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                    },
                    borderRadius: "8px",
                    px: 1,
                    py: 0.6,
                  }}
                >
                  <ArrowForwardIosIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default ParameterFilterPanel;